import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { LeadsService, type Lead } from '@/data/leads.service';
import type { TablesUpdate } from '@/integrations/supabase/types';

export type { Lead };

export function useLeads(filters?: {
  stage?: string;
  temperature?: string;
  search?: string;
}) {
  const { scope, isLoading } = useWorkspaceScope();

  // CRITICAL: Make query key deterministic for proper cache isolation per workspace
  // Use scope.type and workspaceIds/workspaceId instead of scope object
  const queryKey = scope.type === 'single'
    ? ['leads', 'single', scope.workspaceId, filters]
    : ['leads', 'all', scope.workspaceIds.sort().join(','), filters];

  return useQuery({
    queryKey,
    queryFn: async () => {
      // CRITICAL: Validate scope before querying
      if (scope.type === 'all') {
        if (scope.workspaceIds.length === 0) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[useLeads] scope.workspaceIds is empty in all mode - this should not happen', { scope });
          }
          return [];
        }
      } else if (scope.type === 'single') {
        if (!scope.workspaceId) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[useLeads] scope.workspaceId is null in single mode - this should not happen', { scope });
          }
          return [];
        }
      }
      
      const result = await LeadsService.getLeads(scope, filters);
      
      // DEV LOGGING: Track query results
      if (process.env.NODE_ENV === 'development') {
        console.log('[useLeads] Query result:', {
          scope: scope.type === 'single' ? { type: 'single', workspaceId: scope.workspaceId } : { type: 'all', workspaceIds: scope.workspaceIds },
          leadsCount: result.length,
          filters,
        });
      }
      
      return result;
    },
    enabled: !isLoading && (scope.type === 'all' ? scope.workspaceIds.length > 0 : !!scope.workspaceId),
  });
}

export function useUpdateLead() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId, scope } = useWorkspaceScope();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: TablesUpdate<'leads'> }) => {
      // For single mode, use activeWorkspaceId; for all mode, we need to get workspace_id from the lead
      const workspaceId = scope.type === 'single' 
        ? scope.workspaceId 
        : activeWorkspaceId || scope.workspaceIds[0];
      
      if (!workspaceId) throw new Error('No workspace available');
      return LeadsService.updateLead({ id, workspace_id: workspaceId, ...updates });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useTodayDecisions() {
  const { data: leads, isLoading, error } = useLeads();

  const decisions = leads ? generateDecisions(leads) : [];

  return {
    decisions,
    isLoading,
    error,
  };
}

interface Decision {
  id: string;
  leadId: string;
  leadName: string;
  leadPhone: string | null;
  leadEmail: string | null;
  title: string;
  reason: string;
  impactLabel: string;
  impactType: 'high' | 'medium' | 'low';
  priority: number;
  temperature: 'hot' | 'warm' | 'cold';
  pipelineStage: string;
  nextActionType: string | null;
}

function generateDecisions(leads: Lead[]): Decision[] {
  const now = new Date();
  const decisions: Decision[] = [];

  leads.forEach((lead) => {
    // Skip won/lost leads
    if (lead.pipeline_stage === 'won' || lead.pipeline_stage === 'lost') return;

    const lastInteraction = lead.last_interaction_at ? new Date(lead.last_interaction_at) : null;
    const nextAction = lead.next_action_at ? new Date(lead.next_action_at) : null;
    const hoursSinceInteraction = lastInteraction 
      ? (now.getTime() - lastInteraction.getTime()) / (1000 * 60 * 60) 
      : null;

    // Rule 1: HOT leads with overdue next_action_at
    if (lead.temperature === 'hot' && nextAction && nextAction < now) {
      decisions.push({
        id: `overdue-hot-${lead.id}`,
        leadId: lead.id,
        leadName: lead.name,
        leadPhone: lead.phone,
        leadEmail: lead.email,
        title: `Follow up with ${lead.name}`,
        reason: 'Hot lead with overdue action - high conversion potential',
        impactLabel: '€€€',
        impactType: 'high',
        priority: 100,
        temperature: lead.temperature,
        pipelineStage: lead.pipeline_stage || 'new',
        nextActionType: lead.next_action_type,
      });
      return;
    }

    // Rule 2: High score leads not contacted for >24h
    if ((lead.decision_score || 0) >= 70 && hoursSinceInteraction && hoursSinceInteraction > 24) {
      decisions.push({
        id: `high-score-${lead.id}`,
        leadId: lead.id,
        leadName: lead.name,
        leadPhone: lead.phone,
        leadEmail: lead.email,
        title: `Re-engage ${lead.name}`,
        reason: `High-value lead (score: ${lead.decision_score}) not contacted for ${Math.floor(hoursSinceInteraction)}h`,
        impactLabel: '€€',
        impactType: 'high',
        priority: 90,
        temperature: lead.temperature || 'hot', // New leads start as HOT
        pipelineStage: lead.pipeline_stage || 'new',
        nextActionType: lead.next_action_type,
      });
      return;
    }

    // Rule 3: HOT leads without overdue actions (still priority)
    if (lead.temperature === 'hot') {
      decisions.push({
        id: `hot-${lead.id}`,
        leadId: lead.id,
        leadName: lead.name,
        leadPhone: lead.phone,
        leadEmail: lead.email,
        title: `Contact ${lead.name}`,
        reason: 'Hot lead - ready to move forward',
        impactLabel: '€€€',
        impactType: 'high',
        priority: 80,
        temperature: lead.temperature,
        pipelineStage: lead.pipeline_stage || 'new',
        nextActionType: lead.next_action_type,
      });
      return;
    }

    // Rule 4: Warm leads approaching follow-up
    if (lead.temperature === 'warm' && nextAction) {
      const hoursUntilAction = (nextAction.getTime() - now.getTime()) / (1000 * 60 * 60);
      if (hoursUntilAction <= 24 && hoursUntilAction > 0) {
        decisions.push({
          id: `warm-upcoming-${lead.id}`,
          leadId: lead.id,
          leadName: lead.name,
          leadPhone: lead.phone,
          leadEmail: lead.email,
          title: `Prepare for ${lead.name}`,
          reason: `Follow-up scheduled in ${Math.floor(hoursUntilAction)}h`,
          impactLabel: '€€',
          impactType: 'medium',
          priority: 60,
          temperature: lead.temperature,
          pipelineStage: lead.pipeline_stage || 'new',
          nextActionType: lead.next_action_type,
        });
        return;
      }
    }

    // Rule 5: Overdue actions for any temperature
    if (nextAction && nextAction < now) {
      decisions.push({
        id: `overdue-${lead.id}`,
        leadId: lead.id,
        leadName: lead.name,
        leadPhone: lead.phone,
        leadEmail: lead.email,
        title: `Overdue: ${lead.name}`,
        reason: `Action was due ${Math.abs(Math.floor((now.getTime() - nextAction.getTime()) / (1000 * 60 * 60)))}h ago`,
        impactLabel: lead.temperature === 'warm' ? '€€' : '€',
        impactType: lead.temperature === 'warm' ? 'medium' : 'low',
        priority: lead.temperature === 'warm' ? 70 : 50,
        temperature: lead.temperature || 'hot', // New leads start as HOT
        pipelineStage: lead.pipeline_stage || 'new',
        nextActionType: lead.next_action_type,
      });
      return;
    }

    // Rule 6: Cold leads for re-engagement after 7 days
    if (lead.temperature === 'cold' && hoursSinceInteraction && hoursSinceInteraction > 168) {
      decisions.push({
        id: `cold-reengage-${lead.id}`,
        leadId: lead.id,
        leadName: lead.name,
        leadPhone: lead.phone,
        leadEmail: lead.email,
        title: `Re-engage ${lead.name}`,
        reason: 'Cold lead - no contact for 7+ days, worth a retry',
        impactLabel: '€',
        impactType: 'low',
        priority: 20,
        temperature: lead.temperature,
        pipelineStage: lead.pipeline_stage || 'new',
        nextActionType: lead.next_action_type,
      });
    }
  });

  // Sort by priority and limit to 7 decisions
  return decisions
    .sort((a, b) => b.priority - a.priority)
    .slice(0, 7);
}
